import clsx from 'clsx';
import {
  BookOpen,
  Building2,
  ChevronDown,
  FileText,
  GitPullRequest,
  LayoutDashboard,
  LayoutTemplate,
  LogOut,
  Menu,
  Settings,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { APP_NAME, type Capability } from '@imsenta/shared';
import { useSession } from '@/lib/session';
import { LogoTile } from './Logo';
import { Avatar, Badge } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: typeof FileText;
  capability?: Capability;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/catalog', label: 'Catalog', icon: BookOpen },
  // Readers get the catalog; the authoring library is for people who can edit.
  { to: '/documents', label: 'Documents', icon: FileText, capability: 'document:edit' },
  { to: '/change-requests', label: 'Change requests', icon: GitPullRequest, capability: 'changeRequest:comment' },
  { to: '/templates', label: 'Templates', icon: LayoutTemplate, capability: 'template:read' },
  { to: '/settings', label: 'Settings', icon: Settings, capability: 'settings:read' },
];

export function AppShell() {
  const { session, can, logout, switchTenant } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  if (!session) return null;
  const items = NAV.filter((item) => !item.capability || can(item.capability));

  return (
    <div className="flex min-h-full flex-col">
      <header className="pf-no-print sticky top-0 z-40 border-b border-ink-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="-ml-1 rounded p-1.5 text-ink-500 hover:bg-ink-100 lg:hidden"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>

          {/* Signed in, the mark alone identifies the product; the wordmark
              would just repeat it on every page. */}
          <button
            type="button"
            onClick={() => navigate('/')}
            className="flex shrink-0 items-center rounded-md focus-visible:outline-offset-4"
            aria-label={`${APP_NAME} home`}
          >
            <LogoTile size={28} />
          </button>

          <nav className="ml-4 hidden items-center gap-0.5 lg:flex">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13.5px] font-medium transition-colors',
                    isActive ? 'bg-brand-50 text-brand-700' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
                  )
                }
              >
                <item.icon className="size-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2" ref={menuRef}>
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="flex items-center gap-2 rounded-md py-1 pr-1.5 pl-2 hover:bg-ink-100"
              >
                <div className="hidden text-right sm:block">
                  <p className="max-w-40 truncate text-[13px] leading-tight font-medium text-ink-800">{session.tenant?.name ?? 'No organisation'}</p>
                  <p className="text-[11px] leading-tight text-ink-500 capitalize">{session.tenant?.role ?? '—'}</p>
                </div>
                <Avatar name={session.user.name} email={session.user.email} size={30} />
                <ChevronDown className="size-3.5 text-ink-400" />
              </button>

              {menuOpen ? (
                <div className="pf-fade-in absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-xl">
                  <div className="border-b border-ink-100 px-4 py-3">
                    <p className="truncate text-[13.5px] font-semibold text-ink-900">{session.user.name}</p>
                    <p className="truncate text-[12px] text-ink-500">{session.user.email}</p>
                  </div>

                  {session.tenants.length > 1 ? (
                    <div className="border-b border-ink-100 py-1.5">
                      <p className="px-4 py-1 text-[10.5px] font-semibold tracking-wider text-ink-400 uppercase">Organisations</p>
                      {session.tenants.map((tenant) => (
                        <button
                          key={tenant.id}
                          type="button"
                          onClick={async () => {
                            setMenuOpen(false);
                            await switchTenant(tenant.id);
                            navigate('/');
                          }}
                          className="flex w-full items-center gap-2.5 px-4 py-2 text-left hover:bg-ink-50"
                        >
                          <Building2 className="size-4 shrink-0 text-ink-400" />
                          <span className="min-w-0 flex-1 truncate text-[13px] text-ink-700">{tenant.name}</span>
                          {tenant.id === session.tenant?.id ? <Badge tone="brand">current</Badge> : null}
                        </button>
                      ))}
                    </div>
                  ) : null}

                  <div className="py-1.5">
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        navigate('/settings/profile');
                      }}
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13px] text-ink-700 hover:bg-ink-50"
                    >
                      <Settings className="size-4 text-ink-400" /> Your profile
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setMenuOpen(false);
                        await logout();
                        navigate('/signin');
                      }}
                      className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13px] text-rose-600 hover:bg-rose-50"
                    >
                      <LogOut className="size-4" /> Sign out
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {mobileOpen ? (
          <nav className="border-t border-ink-200 bg-white px-3 py-2 lg:hidden">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium',
                    isActive ? 'bg-brand-50 text-brand-700' : 'text-ink-600 hover:bg-ink-100',
                  )
                }
              >
                <item.icon className="size-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        ) : null}
      </header>

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      <footer className="pf-no-print border-t border-ink-200 py-4 text-center text-[12px] text-ink-400">
        {APP_NAME} · policies stored as structured content, exported as Markdown
      </footer>
    </div>
  );
}

/** Page heading with breadcrumb-style context and actions. */
export function PageHeader({
  title,
  description,
  actions,
  meta,
  back,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  meta?: React.ReactNode;
  back?: { to: string; label: string };
}) {
  const navigate = useNavigate();
  return (
    <div className="mb-5">
      {back ? (
        <button
          type="button"
          onClick={() => navigate(back.to)}
          className="mb-2 text-[12.5px] font-medium text-ink-500 hover:text-brand-600"
        >
          ← {back.label}
        </button>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-full sm:basis-80">
          <h1 className="text-xl font-semibold tracking-tight break-words text-ink-900">{title}</h1>
          {description ? <p className="mt-1 text-[13.5px] text-ink-500">{description}</p> : null}
          {meta ? <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div> : null}
        </div>
        {/* Actions wrap and may take the full width on a narrow screen rather
            than forcing the page wider than the viewport. */}
        {actions ? <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">{actions}</div> : null}
      </div>
    </div>
  );
}
